import React, { useEffect, useState } from "react";
import { useAuth } from "../../context/AuthContext";
import AdminPageHeader from "../../components/admin/AdminPageHeader";
import { getClubPlans, updatePlan, type PlanDefinition } from "../../services/plans";

function euros(cents: number, currency: string) {
  return `${(cents / 100).toLocaleString("ro-RO", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} ${currency}`;
}

/**
 * Club account tiers (Demo/Basic/Premium). These are seeded canonical plans;
 * here a platform admin edits the per-athlete price, description and the
 * displayed benefit list. Enforcement of features lives in the code tier matrix,
 * so this page manages pricing + marketing copy, not gating.
 */
export default function ClubPlans() {
  const { activeRole } = useAuth();
  const isPlatformAdmin = activeRole === "platform_admin";

  const [plans, setPlans] = useState<PlanDefinition[]>([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState<string | null>(null);
  const [priceEuros, setPriceEuros] = useState("");
  const [description, setDescription] = useState("");
  const [features, setFeatures] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      setPlans(await getClubPlans());
    } catch (e: any) {
      setError(e?.response?.data?.error || "Nu am putut încărca planurile.");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { if (isPlatformAdmin) load(); else setLoading(false); }, [isPlatformAdmin]);

  const startEdit = (p: PlanDefinition) => {
    setEditing(p.id);
    setPriceEuros(p.price_cents ? (p.price_cents / 100).toString() : "");
    setDescription(p.description || "");
    setFeatures((p.features || []).join("\n"));
    setError(null);
  };

  const save = async (p: PlanDefinition) => {
    setSaving(true); setError(null);
    try {
      await updatePlan(p.id, {
        price_cents: p.code === "demo" ? 0 : Math.round((parseFloat(priceEuros) || 0) * 100),
        description: description.trim() || null,
        features: features.split("\n").map((s) => s.trim()).filter(Boolean),
      });
      setEditing(null);
      await load();
    } catch (e: any) {
      setError(e?.response?.data?.error || "Salvarea a eșuat.");
    } finally {
      setSaving(false);
    }
  };

  if (!isPlatformAdmin) {
    return <div className="p-6"><div className="p-4 text-sm border rounded-xl bg-amber-50 border-amber-200 text-amber-800">Doar administratorii platformei au acces.</div></div>;
  }

  return (
    <div className="p-4 sm:p-6 max-w-4xl mx-auto">
      <AdminPageHeader eyebrow="Planuri" title="Planuri cluburi"
        subtitle="Tier-urile de club (Demo/Basic/Premium). Prețul e per sportiv / lună; facturarea lunară îl folosește automat." />

      {error && <div className="mb-4 p-3 text-sm rounded-xl bg-rose-50 border border-rose-200 text-rose-700">{error}</div>}

      {loading ? (
        <div className="py-10 text-center text-slate-400">Se încarcă…</div>
      ) : (
        <div className="space-y-3">
          {plans.map((p) => (
            <div key={p.id} className="p-4 bg-white border rounded-2xl border-slate-200">
              <div className="flex items-center gap-2">
                <span className="font-bold text-slate-800 capitalize">{p.name}</span>
                <span className="text-[11px] font-mono px-1.5 py-0.5 rounded bg-slate-100 text-slate-500">{p.code}</span>
                <span className="ml-auto font-extrabold text-slate-800">
                  {p.code === "demo" ? "Gratuit" : `${euros(p.price_cents, p.currency)} / sportiv / lună`}
                </span>
              </div>

              {editing === p.id ? (
                <div className="mt-3 space-y-2">
                  {p.code !== "demo" && (
                    <label className="block text-sm">
                      <span className="text-[11px] text-slate-500">Preț / sportiv / lună (EUR)</span>
                      <input type="number" min={0} step="0.01" value={priceEuros} onChange={(e) => setPriceEuros(e.target.value)}
                        className="w-32 px-2 py-1.5 border rounded-lg border-slate-200 block" />
                    </label>
                  )}
                  <label className="block text-sm">
                    <span className="text-[11px] text-slate-500">Descriere</span>
                    <input value={description} onChange={(e) => setDescription(e.target.value)}
                      className="w-full px-2 py-1.5 border rounded-lg border-slate-200 block" />
                  </label>
                  <label className="block text-sm">
                    <span className="text-[11px] text-slate-500">Beneficii (unul pe rând)</span>
                    <textarea rows={4} value={features} onChange={(e) => setFeatures(e.target.value)}
                      className="w-full px-2 py-1.5 border rounded-lg border-slate-200 font-mono text-xs block" />
                  </label>
                  <div className="flex gap-2">
                    <button onClick={() => save(p)} disabled={saving}
                      className="px-3 py-1.5 text-sm font-semibold text-white rounded-lg bg-indigo-600 disabled:opacity-60">
                      {saving ? "Se salvează…" : "Salvează"}
                    </button>
                    <button onClick={() => setEditing(null)} className="px-3 py-1.5 text-sm font-semibold border rounded-lg border-slate-200 text-slate-600">Anulează</button>
                  </div>
                </div>
              ) : (
                <div className="mt-1">
                  {p.description && <p className="text-sm text-slate-500">{p.description}</p>}
                  {p.features && p.features.length > 0 && (
                    <ul className="mt-1 text-xs text-slate-500 flex flex-wrap gap-x-3 gap-y-0.5">
                      {p.features.map((f, i) => <li key={i}>· {f}</li>)}
                    </ul>
                  )}
                  <button onClick={() => startEdit(p)} className="mt-2 px-3 py-1 text-sm font-semibold border rounded-lg border-slate-200 text-slate-600 hover:bg-slate-50">Editează</button>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
